import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { uploadConnectorService, BatchStatus } from '../services/uploadConnector';
import { storeService, Store } from '../services/store';
import { useToast } from '../hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import {
  ArrowLeft,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
  Images,
  RefreshCw,
  Download,
  FileArchive,
  Sparkles,
  Wand2,
  Eraser,
  ArrowUp,
  Palette,
  Play,
  ExternalLink,
  Image as ImageIcon
} from 'lucide-react';

interface TransformationOption {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  credits: number;
  type: 'ai' | 'basic';
}

const TRANSFORMATION_OPTIONS: TransformationOption[] = [
  {
    id: 'background_removal',
    name: 'Background Removal',
    description: 'Remove background with AI precision',
    icon: <Eraser className="w-4 h-4" />,
    credits: 2,
    type: 'ai'
  },
  {
    id: 'upscale_2x',
    name: 'Upscale 2x',
    description: 'Enhance resolution with AI upscaling',
    icon: <ArrowUp className="w-4 h-4" />,
    credits: 3,
    type: 'ai'
  },
  {
    id: 'upscale_4x',
    name: 'Upscale 4x',
    description: 'Maximum resolution enhancement',
    icon: <ArrowUp className="w-4 h-4" />,
    credits: 5,
    type: 'ai'
  },
  {
    id: 'enhance',
    name: 'Auto Enhance',
    description: 'Improve colors and lighting',
    icon: <Sparkles className="w-4 h-4" />,
    credits: 1,
    type: 'basic'
  },
  {
    id: 'optimize',
    name: 'Web Optimize',
    description: 'Optimize for web delivery',
    icon: <Palette className="w-4 h-4" />,
    credits: 0,
    type: 'basic'
  }
];

export function UploadBatchDetailPage() {
  const { batchId } = useParams<{ batchId: string }>();
  const [searchParams] = useSearchParams();
  const connectorId = searchParams.get('connector');
  const navigate = useNavigate();
  const { toast } = useToast();

  const [store, setStore] = useState<Store | null>(null);
  const [batchStatus, setBatchStatus] = useState<BatchStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTransformations, setSelectedTransformations] = useState<string[]>([]);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (connectorId && batchId) {
      loadData();
    } else {
      setError('Missing connector or batch ID');
      setIsLoading(false);
    }

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [connectorId, batchId]);

  // Start polling when processing
  useEffect(() => {
    if (batchStatus?.status === 'processing' && connectorId && batchId) {
      const interval = setInterval(async () => {
        try {
          const status = await uploadConnectorService.getBatchStatusQuick(connectorId, batchId);
          setBatchStatus((prev) => prev ? { ...prev, ...status } : prev);

          if (status.status !== 'processing') {
            clearInterval(interval);
            const fullStatus = await uploadConnectorService.getBatchStatus(connectorId, batchId);
            setBatchStatus(fullStatus);
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }, 2000);

      setPollingInterval(interval);
      return () => clearInterval(interval);
    }
  }, [batchStatus?.status, connectorId, batchId]);

  const loadData = async () => {
    if (!connectorId || !batchId) return;

    try {
      setIsLoading(true);
      setError(null);

      const [storeData, statusData] = await Promise.all([
        storeService.getStore(connectorId),
        uploadConnectorService.getBatchStatus(connectorId, batchId)
      ]);

      setStore(storeData);
      setBatchStatus(statusData);
    } catch (err: any) {
      console.error('Failed to load batch:', err);
      setError(err.message || 'Failed to load batch data');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshStatus = async () => {
    if (!connectorId || !batchId) return;

    try {
      setIsRefreshing(true);
      const statusData = await uploadConnectorService.getBatchStatus(connectorId, batchId);
      setBatchStatus(statusData);
    } catch (err: any) {
      toast({
        title: 'Error',
        description: 'Failed to refresh status',
        variant: 'destructive',
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleTransformationToggle = (transformationId: string) => {
    setSelectedTransformations((prev) =>
      prev.includes(transformationId)
        ? prev.filter((id) => id !== transformationId)
        : [...prev, transformationId]
    );
  };

  const handleProcess = async () => {
    if (!connectorId || !batchId || selectedTransformations.length === 0) return;

    try {
      setIsProcessing(true);

      const transformations = selectedTransformations.map((id) => {
        const option = TRANSFORMATION_OPTIONS.find((o) => o.id === id);
        return { type: id, enabled: true, name: option?.name || id };
      });

      await uploadConnectorService.processBatch(connectorId, batchId, transformations);

      toast({
        title: 'Processing Started',
        description: 'Your images are being processed. This may take a few minutes.',
      });

      await refreshStatus();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to start processing',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadZip = async () => {
    if (!connectorId || !batchId) return;

    try {
      setIsDownloading(true);
      const result = await uploadConnectorService.generateZipDownload(connectorId, batchId);
      window.open(result.url, '_blank');

      toast({
        title: 'Download Ready',
        description: `ZIP file with ${result.imageCount} images is ready`,
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to generate download',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadImage = async (imageId: string) => {
    if (!connectorId) return;

    try {
      const result = await uploadConnectorService.getImageDownloadUrl(connectorId, imageId);
      window.open(result.url, '_blank');
    } catch (err: any) {
      toast({
        title: 'Error',
        description: 'Failed to get download URL',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode; className?: string }> = {
      pending: { variant: 'secondary', icon: <Clock className="w-3 h-3 mr-1" /> },
      uploading: { variant: 'default', icon: <Loader2 className="w-3 h-3 mr-1 animate-spin" /> },
      uploaded: { variant: 'secondary', icon: <CheckCircle className="w-3 h-3 mr-1" /> },
      processing: { variant: 'default', icon: <Loader2 className="w-3 h-3 mr-1 animate-spin" />, className: 'bg-blue-600' },
      completed: { variant: 'default', icon: <CheckCircle className="w-3 h-3 mr-1" />, className: 'bg-green-600' },
      partially_completed: { variant: 'secondary', icon: <AlertCircle className="w-3 h-3 mr-1" /> },
      failed: { variant: 'destructive', icon: <AlertCircle className="w-3 h-3 mr-1" /> },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Badge variant={config.variant} className={`flex items-center ${config.className || ''}`}>
        {config.icon}
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const calculateTotalCredits = () => {
    const imageCount = batchStatus?.stats.total || 0;
    return selectedTransformations.reduce((total, id) => {
      const option = TRANSFORMATION_OPTIONS.find((o) => o.id === id);
      return total + (option?.credits || 0) * imageCount;
    }, 0);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 mx-auto text-destructive mb-4" />
          <h3 className="text-lg font-medium mb-2">Error</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => navigate('/uploads')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Uploads
          </Button>
        </div>
      </div>
    );
  }

  const isCompleted = batchStatus?.status === 'completed' || batchStatus?.status === 'partially_completed';
  const canProcess = batchStatus?.status === 'pending' && selectedTransformations.length > 0;
  const canDownload = isCompleted && (batchStatus?.stats.processed || 0) > 0;

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/uploads?connector=${connectorId}`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{batchStatus?.name || 'Batch Details'}</h1>
              {batchStatus && getStatusBadge(batchStatus.status)}
            </div>
            <p className="text-muted-foreground">
              {store?.name} &bull; Created {batchStatus && formatDistanceToNow(new Date(batchStatus.createdAt), { addSuffix: true })}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refreshStatus}
          disabled={isRefreshing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Images className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Images</p>
                <p className="text-2xl font-bold">{batchStatus?.stats.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Processed</p>
                <p className="text-2xl font-bold">{batchStatus?.stats.processed || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{batchStatus?.stats.pending || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-destructive/10 rounded-lg">
                <AlertCircle className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Failed</p>
                <p className="text-2xl font-bold">{batchStatus?.stats.failed || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Processing Progress */}
      {batchStatus?.status === 'processing' && (
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <div className="flex-1">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Processing images...</span>
                  <span className="text-sm text-muted-foreground">{batchStatus.progress}%</span>
                </div>
                <Progress value={batchStatus.progress} className="h-2" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content - Images Grid */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Images className="w-5 h-5" />
                Images
              </CardTitle>
              <CardDescription>
                {batchStatus?.images?.length || 0} images in this batch
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!batchStatus?.images || batchStatus.images.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No images in this batch</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-h-[600px] overflow-y-auto p-1">
                  {batchStatus.images.map((image) => (
                    <div
                      key={image.id}
                      className="relative group aspect-square rounded-lg overflow-hidden border bg-muted"
                    >
                      <img
                        src={image.processed_url || image.original_url}
                        alt={image.original_filename}
                        className="w-full h-full object-cover"
                      />

                      {image.status === 'processing' && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <Loader2 className="w-6 h-6 animate-spin text-white" />
                        </div>
                      )}

                      {image.status === 'failed' && (
                        <div className="absolute inset-0 bg-destructive/80 flex items-center justify-center">
                          <AlertCircle className="w-6 h-6 text-white" />
                        </div>
                      )}

                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleDownloadImage(image.id)}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                        {image.processed_url && (
                          <a
                            href={image.processed_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-white/80 hover:text-white flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Open Full Size
                          </a>
                        )}
                      </div>

                      {image.status === 'completed' && (
                        <div className="absolute top-2 right-2">
                          <Badge className="bg-green-600 text-white text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Done
                          </Badge>
                        </div>
                      )}

                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                        <p className="text-xs text-white truncate">{image.original_filename}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Transformations Selection */}
          {batchStatus?.status === 'pending' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5" />
                  Transformations
                </CardTitle>
                <CardDescription>
                  Select the transformations to apply
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {TRANSFORMATION_OPTIONS.map((option) => (
                  <div
                    key={option.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedTransformations.includes(option.id)
                        ? 'bg-primary/10 border-primary'
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => handleTransformationToggle(option.id)}
                  >
                    <Checkbox
                      checked={selectedTransformations.includes(option.id)}
                      onCheckedChange={() => handleTransformationToggle(option.id)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {option.icon}
                        <Label className="font-medium cursor-pointer">{option.name}</Label>
                        {option.type === 'ai' && (
                          <Badge variant="secondary" className="text-xs">AI</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{option.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {option.credits} credits per image
                      </p>
                    </div>
                  </div>
                ))}

                {selectedTransformations.length > 0 && (
                  <div className="pt-4 border-t">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Images:</span>
                      <span>{batchStatus?.stats.total || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-4">
                      <span className="text-muted-foreground">Total Credits:</span>
                      <span className="font-bold">{calculateTotalCredits()}</span>
                    </div>
                    <Button
                      className="w-full"
                      onClick={handleProcess}
                      disabled={isProcessing || !canProcess}
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Starting...
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Start Processing
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Applied Transformations */}
          {batchStatus?.status !== 'pending' && batchStatus?.transformations && batchStatus.transformations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5" />
                  Applied Transformations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {batchStatus.transformations.map((t: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>{t.name || t.type}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Credits Used:</span>
                    <span className="font-bold">{batchStatus.creditsUsed || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Download Options */}
          {canDownload && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Download
                </CardTitle>
                <CardDescription>
                  Download your processed images
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  className="w-full"
                  onClick={handleDownloadZip}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating ZIP...
                    </>
                  ) : (
                    <>
                      <FileArchive className="w-4 h-4 mr-2" />
                      Download All as ZIP
                    </>
                  )}
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Or click on individual images to download
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
